import type { HealthHistoryChange } from '@ofmos/shared/health-history';
import { cx, KpiTile, STATUS_INK, StatusSymbol } from '../../../ui';
import { FULFILLMENT, HealthChangesList } from '../shared';
import type { Focus } from './FocusClients';

/**
 * THE KPI ROW (brief section 4; mockup `:147-174`; components page section 3): four tiles, no
 * Creators tile (D9), the grey fill on this row only (L4), FE-FOUNDATION's `KpiTile`.
 *
 * The two client tiles are a single-choice pair that picks the list below (they replace the
 * focus segmented control and keep its `?focus=yellow`); In onboarding and Money overdue open
 * their page. Each client tile counts every client its list holds, so a handled or paused client
 * leaves the tile with the row. "+N since last week" and the line come from the colour history
 * alone - its count today minus its count a week ago, one set by one rule - and are never
 * subtracted from the tile's number (FE-DB-12); "+N" opens the names of the clients who turned
 * that colour (D10, Q-FND-1 A).
 */
export interface KpiTrend {
  /** The history's count today minus its count a week ago. */
  readonly delta: number;
  /** The history's daily counts, oldest first (a neutral line, R-K1). */
  readonly series: readonly number[];
  /** The clients who turned this colour since a week ago. */
  readonly entered: readonly HealthHistoryChange[];
}

export interface KpiRowProps {
  readonly focus: Focus;
  readonly onFocus: (next: Focus) => void;
  readonly needsAttention: number;
  /** Null without the history (or a day of it): no delta, no line. */
  readonly needsAttentionTrend: KpiTrend | null;
  readonly keepAnEye: number;
  readonly keepAnEyeTrend: KpiTrend | null;
  readonly onboarding: { readonly total: number; readonly note: string | null };
  /** Absent when this installation has no payments; `value` null for a person who may not read them. */
  readonly money?:
    | { readonly value: string | null; readonly note: string | null; readonly to: string }
    | undefined;
  readonly className?: string | undefined;
}

/** The note line's delta, its words and the names behind it, when the history has the day. */
function trendProps(trend: KpiTrend | null) {
  if (trend === null) return {};
  return {
    delta: trend.delta,
    note: 'since last week',
    sparkline: trend.series,
    ...(trend.entered.length > 0 ? { changes: <HealthChangesList changes={trend.entered} /> } : {}),
  };
}

export function KpiRow(props: KpiRowProps) {
  return (
    <div
      className={cx('grid grid-cols-2 gap-4 md:grid-cols-[1.5fr_1fr_1fr_1fr]', props.className)}
      data-kpi-row=""
    >
      <KpiTile
        mode="pick"
        lead
        label={FULFILLMENT.health.levels.red}
        mark={<StatusSymbol status="act-now" />}
        value={props.needsAttention}
        valueTone="act-now"
        {...trendProps(props.needsAttentionTrend)}
        selected={props.focus === 'red'}
        onSelect={() => props.onFocus('red')}
      />
      <KpiTile
        mode="pick"
        label={FULFILLMENT.health.levels.yellow}
        mark={<StatusSymbol status="watch" />}
        value={props.keepAnEye}
        {...trendProps(props.keepAnEyeTrend)}
        selected={props.focus === 'yellow'}
        onSelect={() => props.onFocus('yellow')}
      />
      <KpiTile
        mode="link"
        label="In onboarding"
        value={props.onboarding.total}
        {...(props.onboarding.note === null
          ? {}
          : {
              note: (
                <span className={cx('font-semibold', STATUS_INK.late)}>
                  {props.onboarding.note}
                </span>
              ),
            })}
        to="/fulfillment/onboarding"
      />
      {props.money !== undefined && (
        <KpiTile
          mode="link"
          label="Money overdue"
          mark={<StatusSymbol status="act-now" />}
          value={props.money.value}
          {...(props.money.note === null ? {} : { note: props.money.note })}
          to={props.money.to}
        />
      )}
    </div>
  );
}
